'use client';

import {
  useState, useCallback, useEffect,
} from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ComboBox } from '@components/common/Forms';
import { fetcher, isApiV1Errors } from '@utils/data-fetching';
import { fetchSearchData } from '@components/templates/job/search/searchFetcher';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { bjToast } from '@components/common';
import { DocumentInterface } from '@type/v1-api-types';
import { ExternalGetQueryFunction } from '@type/form-types';
import { UserSearch } from '@type/v1-api-types/user-search';
import { isApiUserSearchOk } from '../../../type-predicates/v1-api-predicates/user-search';

interface SearchResult {
  category?: string;
  recent?: string;
  suggestion?: string;
}

interface CandidateJobSearchKeywordProps {
  locale: Locale;
  value?: string;
  onChange: (value: string) => void;
}

/**
 * @description Keyword input field for the candidate job search form.
 * Shows recent/saved searches on focus and live autocomplete suggestions while typing.
 */
export const CandidateJobSearchKeyword = ({ locale, value = '', onChange }: CandidateJobSearchKeywordProps) => {
  const { t } = useTranslation('common');

  const [dropdownList, setDropdownList] = useState<SearchResult[]>([]);
  const [searchStatus, setSearchStatus] = useState<'recent' | 'suggestion'>('recent');
  const [recentSearch, setRecentSearch] = useState<SearchResult[]>([]);

  const externalSearchQuery = useDebounce<ExternalGetQueryFunction>((keyword: string) => fetchSearchData(keyword, '', '').then((res) => {
    setDropdownList(res.jobSearchGroup.filter((item) => !!(item as DocumentInterface)?.suggestion));
    setSearchStatus('suggestion');
  }), 350);

  const loadRecentSearches = useCallback(() => {
    const popularSearches: SearchResult[] = [
      { category: t('user.popular.searches') },
      { recent: t('popular.search.filters.driver') },
      { recent: t('popular.search.filters.engineer') },
      { recent: t('popular.search.filters.manager') },
      { recent: t('popular.search.filters.part.time') },
      { recent: t('popular.search.filters.sales') },
      { recent: t('popular.search.filters.remote') },
    ];

    fetcher<UserSearch>('/v1/job-search-tooltip', locale, { method: 'GET' })
      .then((response) => {
        if (isApiUserSearchOk(response)) {
          const historySuggestion: SearchResult[] = [];

          if (response.recentSearches?.length) {
            historySuggestion.push(
              { category: t('user.recent.searches') },
              ...response.recentSearches.map((s) => ({
                recent: (s.keyword || '') + (s.location ? ` in ${s.location}` : ''),
              })),
            );
          }

          if (response.savedSearches?.length) {
            historySuggestion.push({ category: t('user.saved.searches') });
            response.savedSearches.forEach((item) => {
              const { keyword, location } = item.jobSearch;
              if (keyword && location?.[0]) {
                historySuggestion.push({ recent: `${keyword} in ${location[0]}` });
              } else if (keyword) {
                historySuggestion.push({ recent: keyword });
              }
            });
          }

          const list = historySuggestion.length ? historySuggestion : popularSearches;
          setDropdownList(list);
          setRecentSearch(list);
          return;
        }

        if (isApiV1Errors(response)) {
          bjToast.error(response.errors[0].message);
        }

        setDropdownList(popularSearches);
        setRecentSearch(popularSearches);
      })
      .catch(() => {
        setDropdownList(popularSearches);
        setRecentSearch(popularSearches);
      });
  }, [locale, t]);

  useEffect(() => {
    loadRecentSearches();
  }, [loadRecentSearches]);

  useEffect(() => {
    if (!value) {
      setSearchStatus('recent');
      setDropdownList(recentSearch);
    }
  }, [value, recentSearch]);

  return (
    <div className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 z-1 size-5 -translate-y-1/2 text-ink-medium" />
      <ComboBox
        name="candidate_search_keyword"
        id="candidate_search_keyword"
        defaultValue={value}
        placeholder={t('e.g. driver')}
        className="h-12 w-full rounded-2xl border-input/65 bg-surface !pl-12 text-sm text-ink placeholder:text-ink-light focus:!border-secondary focus:!ring-secondary/20"
        dropdownContainerClass="z-9 bottom-full mb-2 max-h-72 rounded-2xl border border-input/60 bg-surface py-2 shadow-lg overflow-hidden"
        options={{
          showDropdownButton: false,
          showResetButton: !!value,
          valueFromKey: searchStatus,
          staticList: dropdownList,
          externalGetQuery: externalSearchQuery,
        }}
        onChange={(e) => onChange(e.target.value)}
        onClear={() => onChange('')}
        onInputSubmit={(val) => {
          if (val?.trim()) onChange(val.trim());
        }}
        onSelectedItemChange={(item) => {
          if (!item) return;
          const result = item as SearchResult;
          const keyword = result.suggestion ?? result.recent ?? '';
          if (keyword) onChange(keyword);
        }}
        renderList={(item, index, listKey, getItemProps, listStyling) => {
          const result = item as SearchResult;
          if (result.category) {
            return (
              <li key={listKey} className="py-1.5 pl-5 text-xs font-semibold text-ink-light">
                {result.category}
              </li>
            );
          }
          return (
            <li
              key={listKey}
              className={`${listStyling} !cursor-pointer !border-0 !py-1 !pl-5 text-sm`}
              {...getItemProps({ item, index })}
            >
              {result[searchStatus as keyof SearchResult]}
            </li>
          );
        }}
      />
    </div>
  );
};
